/**
 * Dense linear algebra, for systems of a dozen unknowns.
 *
 * Nothing clever, on purpose: these systems are tiny and the answers are the
 * physics, so accuracy costs nothing worth saving and iterative methods would
 * put a tolerance between the reader and their own arithmetic.
 */

/** Gaussian elimination with partial pivoting. Null if singular. */
export function solveLinear(A: number[][], b: number[]): number[] | null {
    const n = b.length;
    if (n === 0) return [];
    const M = A.map((row, i) => [...row, b[i]]);
    for (let col = 0; col < n; col++) {
        let piv = col;
        for (let r = col + 1; r < n; r++) if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
        if (Math.abs(M[piv][col]) < 1e-10) return null;
        if (piv !== col) { const t = M[piv]; M[piv] = M[col]; M[col] = t; }
        for (let r = 0; r < n; r++) {
            if (r === col) continue;
            const f = M[r][col] / M[col][col];
            if (f === 0) continue;
            for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
        }
    }
    const x = new Array<number>(n);
    for (let i = 0; i < n; i++) x[i] = M[i][n] / M[i][i];
    return x;
}

/**
 * Solve `A x = b` when `A` may not have full rank — the answer with the
 * undetermined coordinates set to ZERO.
 *
 * `solveLinear` gives up the moment a pivot vanishes, and for this engine that
 * is the wrong answer to a real question. A MASSLESS body's row is `0·a = ΣF`:
 * it says nothing about its acceleration, which is decided entirely by what it
 * is tied to. Where the ropes decide it, the row has a pivot and nothing
 * changes. Where they do not — a weightless wheel in a direction nothing pulls
 * it along — the coordinate is genuinely undetermined, and the one answer that
 * is always right for a body with no inertia is that it does not accelerate.
 *
 * `null` only when the system is INCONSISTENT: a row that reduces to
 * `0 = something`. That is not an undetermined coordinate, it is a demand that
 * cannot be met, and the caller is owed the difference.
 */
export function solveFree(A: number[][], b: number[], tol = 1e-10): number[] | null {
    const rows = A.length;
    const cols = rows ? A[0].length : 0;
    const M = A.map((row, i) => [...row, b[i]]);
    const pivotOf: number[] = [];        // column pivoted on, per row used
    let r = 0;
    for (let c = 0; c < cols && r < rows; c++) {
        let best = r;
        for (let k = r + 1; k < rows; k++) if (Math.abs(M[k][c]) > Math.abs(M[best][c])) best = k;
        if (Math.abs(M[best][c]) < tol) continue;          // no pivot here: a free column
        if (best !== r) { const t = M[best]; M[best] = M[r]; M[r] = t; }
        for (let k = 0; k < rows; k++) {
            if (k === r) continue;
            const f = M[k][c] / M[r][c];
            if (f === 0) continue;
            for (let j = c; j <= cols; j++) M[k][j] -= f * M[r][j];
        }
        pivotOf[r] = c;
        r++;
    }
    // Any row left with no pivot must read 0 = 0, or there is no solution.
    for (let k = r; k < rows; k++) {
        if (Math.abs(M[k][cols]) > 1e-7 * (1 + Math.abs(b[k]))) return null;
    }
    const x = new Array<number>(cols).fill(0);
    for (let k = 0; k < r; k++) x[pivotOf[k]] = M[k][cols] / M[k][pivotOf[k]];
    return x;
}

/**
 * The smallest move that satisfies `A x = b` — least-norm, for a system with
 * more coordinates than rows.
 *
 * "Move the hand a metre; how far does the load go" is such a question once
 * parts have two coordinates each: the ropes pin the answer along themselves
 * and say nothing across, and the honest reading of "nothing says" is "it does
 * not move that way".
 */
export function leastNorm(A: number[][], b: number[]): number[] | null {
    const m = A.length;
    if (!m) return [];
    const n = A[0].length;
    const G = A.map(ri => A.map(rj => ri.reduce((s, v, k) => s + v * rj[k], 0)));
    for (let i = 0; i < m; i++) G[i][i] += 1e-12;
    const mu = solveLinear(G, b);
    if (!mu) return null;
    return Array.from({ length: n }, (_, k) => A.reduce((s, row, i) => s + row[k] * mu[i], 0));
}

/** The smallest t > 0 at which a + b·t + ½c·t² crosses zero downwards. */
export function firstDownwardRoot(a: number, b: number, c: number, tol = 1e-9): number | null {
    // Nothing is moving and nothing is accelerating: this row has no future.
    // Without the guard, floating-point dust in a resting system produces a
    // root some tens of millions of seconds away and the run "ends" there.
    if (Math.abs(b) < 1e-9 && Math.abs(c) < 1e-9) return null;
    const half = c / 2;
    const roots: number[] = [];
    if (Math.abs(half) < 1e-12) {
        if (Math.abs(b) > 1e-12) roots.push(-a / b);
    } else {
        const disc = b * b - 4 * half * a;
        if (disc < 0) return null;
        const s = Math.sqrt(disc);
        roots.push((-b + s) / (2 * half), (-b - s) / (2 * half));
    }
    // Downwards only: the value must be on its way through zero, not bouncing
    // off it, or a constraint already resting at its limit fires every step.
    const good = roots
        .filter(t => t > tol && Number.isFinite(t))
        .filter(t => b + c * t < -tol || (Math.abs(b + c * t) <= tol && c < 0));
    return good.length ? Math.min(...good) : null;
}
